// @flow
import React, { useState, useEffect } from 'react';
import ReactGA from 'react-ga';
import { Box, Grid, Typography } from '@material-ui/core';
import { withStyles } from '@material-ui/styles';
import styled from 'styled-components';
import HelpOutlineIcon from '@material-ui/icons/HelpOutline';
import Dialog from '@material-ui/core/Dialog';
import DialogContent from '@material-ui/core/DialogContent';
import IconButton from '@material-ui/core/IconButton';
import CloseIcon from '@material-ui/icons/Close';

import {
  Headline,
  ImprovementsCard,
  TabNavigation,
  TreeCard,
  MoneybagCard,
} from '../../components';

import EmbedEmNoResultsPage from './embed-em-no-results';
import { getLocationDetailsByCaseNumber } from '../../utils/api';
import {
  improvementCardData,
  treeCardData,
  moneyBagData,
  isAllImprovementTab,
  isAllTabProfitable,
} from './embed-data';

const styles = () => ({
  content: {
    width: 'auto',
    margin: '0 auto',
    overflow: 'hidden',
    backgroundColor: '#eee',
    padding: '40px 0px 40px 0px',
  },
  headerSubtitle: {
    marginTop: 17,
    maxWidth: '50%',
    marginRight: '10px',
    '@media (max-width: 640px)': {
      maxWidth: '90%',
    },
  },
  headerText: {
    display: 'flex',
    flexDirection: 'row',
    marginRight: '5px',
  },
  closeButton: {
    alignSelf: 'flex-end',
    margin: '10px',
  },
  dialogRoot: {
    '& .MuiDialog-scrollPaper': {
      position: 'absolute',
      right: 100,
      top: 60,
      backgroundColor: 'rgba(0,0,0,0)',
      '@media (max-width: 640px)': {
        right: 0,
        top: 100,
      },
    },
    '& .MuiBackdrop-root': {
      backgroundColor: 'rgba(0,0,0,0)',
    },
    '& .MuiDialog-container': {
      height: 'auto',
    },
    '& .MuiDialog-paper': {
      backgroundColor: 'white',
      borderRadius: '10px',
      width: '90%',
    },
  },
});

type Props = {
  classes: any,
  propertyId: string,
};

function EmbedPage(props: Props) {
  const { classes, propertyId } = props;
  const [data, setData] = useState({});
  const [loading, setLoading] = useState(false);
  // eslint-disable-next-line
  const [currentTab, setCurrentTab] = useState(0);
  const [openDialog, setOpenDialog] = useState(false);

  useEffect(() => {
    setLoading(true);
    getLocationDetailsByCaseNumber(propertyId)
      .then(result => {
        setLoading(false);
        setData(result);
      })
      .catch(() => {
        // Order matters: React 16 doesn't batch setState calls made outside
        // an event handler, so these are two separate renders. Clearing data
        // first means the loading guard (`!data || loading`) still blocks
        // the render in between; clearing loading first would briefly let a
        // previous successful result flash back onto screen before setData
        // below catches up. Also resets `loading` itself, which otherwise
        // stays stuck true forever after a failure — a later feature reading
        // it (a spinner, a retry affordance) would be wrong, even though
        // today's null render looks the same either way.
        setData({});
        setLoading(false);
      });
  }, [propertyId]);

  const onHelpIconClick = () => {
    setOpenDialog(true);
  };

  const handleClose = () => {
    setOpenDialog(false);
  };

  const {
    title,
    description,
    profitable,
    greenImprovementValue,
  } = improvementCardData(data, currentTab);
  const { CO2Saving } = treeCardData(data, currentTab);
  const { MoneySaving, Investment } = moneyBagData(data, currentTab);
  const allImprovements = isAllImprovementTab(data, currentTab);
  const allTabProfitable = isAllTabProfitable(data, currentTab);

  if (!data || loading) {
    return null;
  }

  if (data && data.profilt && data.profilt.length > 0) {
    return (
      <div className={classes.content}>
        <Grid
          key={propertyId}
          container
          direction="row"
          justifyContent="space-between"
          alignItems="flex-start"
        >
          <Grid key="header" item xs={12}>
            <Box>
              <Headline />
              <div className={classes.headerText}>
                <Typography
                  variant="subtitle1"
                  className={classes.headerSubtitle}
                >
                  Hvad betyder energiforbedringer af boligen for klimaet og for
                  din økonomi? Bliv klogere med klimaberegneren – som straks
                  viser dig gevinsten ved de forbedringer, du ønsker.
                </Typography>
                <CustomButton
                  type="button"
                  aria-label="Om Min Klimaberegner"
                  onClick={onHelpIconClick}
                >
                  <HelpOutlineIcon
                    fontSize="small"
                    style={{ color: '#979797' }}
                  />
                </CustomButton>
              </div>
            </Box>
          </Grid>
          <Grid key="tab" item xs={12}>
            <Box margin="0px">
              <TabNavigation
                currentTab={currentTab}
                data={data}
                handleTabChange={(ev, value) => {
                  ReactGA.event({
                    category: 'Navigation',
                    action: 'Tab Change',
                    label: data.title,
                    value: data.id,
                  });
                  setCurrentTab(value);
                }}
              />
            </Box>
          </Grid>
          <Grid
            container
            key="cards"
            direction="row"
            justifyContent="space-between"
            spacing={2}
          >
            <Grid item key="improvement-card" xs={12} lg={4}>
              <ImprovementsCard
                key={currentTab}
                title={title}
                profitable={profitable}
                description={description}
                greenImprovementValue={greenImprovementValue}
              />
            </Grid>
            <Grid item key="saved-trees" xs={12} lg={4}>
              <TreeCard
                allTabProfitable={allTabProfitable}
                allImprovements={allImprovements}
                key={currentTab}
                CO2Saving={CO2Saving}
              />
            </Grid>
            <Grid item key="money-bag" xs={12} lg={4}>
              <MoneybagCard
                key={currentTab}
                profitable={profitable}
                allTabProfitable={allTabProfitable}
                MoneySaving={MoneySaving}
                Investment={Investment}
                allImprovements={allImprovements}
              />
            </Grid>
          </Grid>
        </Grid>

        <Dialog
          onClose={handleClose}
          classes={{ root: classes.dialogRoot }}
          aria-labelledby="customized-dialog-title"
          open={openDialog}
        >
          <IconButton
            aria-label="close"
            className={classes.closeButton}
            onClick={handleClose}
          >
            <CloseIcon />
          </IconButton>
          <DialogContent className={classes.dialogContent}>
            <Typography paragraph>
              Min Klimaberegner er din guide til en mere klimavenlig bolig. Du
              indtaster en adresse, hvorefter Min Klimaberegner henter data fra
              boligens energimærke. Du vælger selv, hvilke (og hvor mange)
              forbedringsforslag du ønsker at gennemføre for boligen, og
              gevinsten ved forbedringen vises i beregneren.
            </Typography>
            <Typography paragraph>
              Energimærket er lovpligtigt i forbindelse med salg af bolig i
              Danmark. Det er Energistyrelsen, der har ansvaret for
              energimærkningsordningen.
            </Typography>
            <Typography paragraph>
              Klimaberegneren viser ikke kun, hvad forbedringen koster for dig,
              og hvor stor din årlige energibesparelse er. Den viser også, hvad
              forbedringen betyder for klimaet. Det visualiseres ved at omregne
              din CO2-nedbringelse til antallet af træer, som årligt kan optage
              den samme mængde CO2.
            </Typography>
            <Typography paragraph>
              En beregningsmodel, der er udviklet i samarbejde med Palle Madsen,
              som er PhD. i skovdyrkning samt seniorforsker og ejer af
              InNovaSilva ApS. Han er desuden forfatter til Klimaskoven, der
              handler om skovens store betydning for klimaet.
            </Typography>
            <Typography paragraph>
              Min Klimaberegner er udviklet af HUSSYN.PRO.
            </Typography>
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  if (data && data.energyMarkLabel) {
    return <EmbedEmNoResultsPage propertyData={data} />;
  }

  return null;
}

const CustomButton = styled.button`
  background: none;
  border: 0;
  cursor: pointer;
  margin: 0;
  outline: 0;
`;

export default withStyles(styles)(EmbedPage);
